import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Checkbox, Divider, Alert, Chip,
} from '@mui/material';

/**
 * Visar AI:ns förslag som en diff. Inget skrivs över förrän användaren
 * godkänner – originalet ligger kvar i profilen hela tiden.
 */
export default function CompactAIDialog({ open, onClose, changes, note, onApply }) {
  const [accepted, setAccepted] = useState({});

  useEffect(() => {
    if (!open) return;
    // Textnedkortningar förvalda, strykningar av hela projekt inte.
    setAccepted(Object.fromEntries(
      changes.map(c => [`${c.userId}:${c.key}`, c.field !== 'drop']),
    ));
  }, [open, changes]);

  const byPerson = changes.reduce((acc, c) => {
    (acc[c.userId] = acc[c.userId] || { name: c.personName, items: [] }).items.push(c);
    return acc;
  }, {});

  const acceptedCount = Object.values(accepted).filter(Boolean).length;

  const apply = () => {
    onApply(changes.filter(c => accepted[`${c.userId}:${c.key}`]));
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>AI-nedkortning – granska innan du använder</DialogTitle>
      <DialogContent dividers>
        {note && <Alert severity="info" sx={{ mb: 2 }}>{note}</Alert>}

        {changes.length === 0 && (
          <Typography variant="body2">
            AI:n hittade inget att korta – texterna ryms redan.
          </Typography>
        )}

        {Object.entries(byPerson).map(([userId, group]) => (
          <Box key={userId} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold">{group.name}</Typography>
            <Divider sx={{ mb: 1 }} />
            {group.items.map(c => {
              const key = `${c.userId}:${c.key}`;
              return (
                <Box key={key} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                  <Checkbox size="small" checked={!!accepted[key]}
                    onChange={e => setAccepted(a => ({ ...a, [key]: e.target.checked }))} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {c.label}
                      {c.field === 'drop' && (
                        <Chip size="small" label="Strykning" color="warning"
                          variant="outlined" sx={{ ml: 1, height: 18 }} />
                      )}
                    </Typography>
                    <Typography variant="body2"
                      sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
                      {c.from || '(tomt)'}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">{c.to}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant="contained" onClick={apply} disabled={acceptedCount === 0}>
          Använd {acceptedCount} ändring{acceptedCount === 1 ? '' : 'ar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
